import i18n from 'i18next';

import { DEFAULT_TITLE, VERSIONS } from '@common/constants';
import { ApiUrl } from '@common/enums';
import { makeEEUrlWithNewCode } from '@common/helpers';
import { Estimate } from '@common/models';
import {
    EstimateStatus,
    GetEstimateDto,
    SaveEstimateDto,
    SaveEstimateResponse,
} from '@common/models/save-estimate-dto';
import { convertToVersion, replacerV2, reviver } from '@common/utils/estimate.utils';
import { NotifierType, showNotification } from '@core/components';
import { easyEstimatorApi } from '@ee-api/easyEstimatorService';

export type EstimateQueryParams = {
    params?: Partial<{ code: string; customerEmail: string; estimateNumber: string }>;
};
export type EstimateStatusParams = {
    params: { code: string };
};

const estimateService = easyEstimatorApi.injectEndpoints({
    endpoints: (builder) => ({
        /**
         * fetch an existing estimate using queryFn for error handling and notification immediately upon fetching
         * results. This eliminates the need for a problematic useEffect
         */
        fetchEstimate: builder.query<GetEstimateDto, EstimateQueryParams>({
            async queryFn(args, _api, _extraOptions, baseQuery) {
                const { ...result } = await baseQuery({
                    url: args?.params?.code ? ApiUrl.ESTIMATE_CODE : ApiUrl.ESTIMATE_NUMBER,
                    method: 'GET',
                    params: args.params,
                });
                // if there's ANY error and no data, notify failed estimate fetch and return as if it was a successful fetch, but with no data
                if (result?.error && !result?.data) {
                    document.title = DEFAULT_TITLE;
                    showNotification({
                        type: NotifierType.ERROR,
                        title: i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.NOT_FOUND.TITLE'),
                        description: i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.NOT_FOUND.DESC'),
                        message: i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.NOT_FOUND.MSG'),
                        duration: 30,
                    });
                    const newUrl = makeEEUrlWithNewCode({ loadEstimate: undefined });
                    window.history.pushState({ path: newUrl.toString() }, '', newUrl.toString());
                    return { data: result?.data };
                }
                if (!result?.data) {
                    return { error: result?.error };
                }
                const { estimateData, ...rest } = JSON.parse(JSON.stringify(result?.data), reviver);
                const convertedEstimateData = convertToVersion(estimateData as Estimate, VERSIONS.V2);
                // const v3 = convertToVersion(estimateData as Estimate, VERSIONS.V3);

                return {
                    data: {
                        ...rest,
                        estimateData: convertedEstimateData,
                    },
                };
            },
            extraOptions: { maxRetries: 0 },
        }),
        /**
         * This is the original, generic implementation of fetchEstimate query
         * It does not implement the queryFn
         */
        standardFetchEstimate: builder.query<GetEstimateDto, EstimateQueryParams>({
            query: ({ params }) => ({
                url: params?.code ? ApiUrl.ESTIMATE_CODE : ApiUrl.ESTIMATE_NUMBER,
                method: 'GET',
                params,
            }),
            transformResponse: (data: GetEstimateDto) => {
                return JSON.parse(JSON.stringify(data), reviver);
            },
            extraOptions: { maxRetries: 1 },
        }),
        fetchEstimateStatus: builder.query<EstimateStatus, EstimateStatusParams>({
            query: ({ params }) => ({
                url: ApiUrl.ESTIMATE_STATUS,
                method: 'GET',
                params,
            }),
        }),
        createEstimate: builder.mutation<SaveEstimateResponse, SaveEstimateDto>({
            query: (data) => {
                const dataAsJson = JSON.stringify(data, replacerV2);
                return {
                    url: ApiUrl.ESTIMATE,
                    method: 'POST',
                    data: dataAsJson,
                };
            },
            transformResponse: (data: SaveEstimateResponse) => {
                //TODO Remove this transform - this is just to test error handling!!!!
                // return { ...data, estimateNumber: '' } as SaveEstimateResponse; // no estimate Number if email mismatch
                return data;
            },
        }),
        saveEstimateMultiPart: builder.mutation<SaveEstimateResponse, FormData>({
            query: (formData) => ({
                url: ApiUrl.ESTIMATE_MULTIPART,
                method: 'POST',
                data: formData,
                isMultipartForm: true,
            }),
        }),
    }),
});

export const {
    useLazyFetchEstimateQuery,
    useFetchEstimateQuery,
    useFetchEstimateStatusQuery,
    useCreateEstimateMutation,
    useSaveEstimateMultiPartMutation,
} = estimateService;
